import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, Flame } from 'lucide-react';
import { analytics } from '@/lib/analytics';

// Module-level Map keyed by history.state.key so React StrictMode's double-mount
// in development doesn't synthesize two different upgrade_intent_ids for the
// same direct-visit. Lives outside the component to survive remounts. Cleared
// when the entry leaves history (we never grow this unbounded in practice —
// only a handful of /upgrade visits exist per session).
const directVisitIntentMap = new Map<string, string>();

// ────────────────────────────────────────────────────────────────────
// Types & fetch helpers (mirror Settings.tsx; could be lifted to /lib later)
// ────────────────────────────────────────────────────────────────────

type Tier = 'pro_monthly' | 'pro_yearly' | 'founding_yearly';

interface UsageBucket { used: number; limit: number | null; }
interface UsageResponse {
  is_pro: boolean;
  saves: UsageBucket;
  ai_message: UsageBucket;
  mcp_call: UsageBucket;
  upgrade_url: string;
}

// Hard-coded Saver caps used in the downgrade-impact modal. These are the
// limits the user will face after the cancellation flips them to Saver.
// Mirrors lib/billing.js cap matrix; if those change, update here too.
const SAVER_CAPS = { saves: 100, ai_message: 20, mcp_call: 10 } as const;

interface BillingStatus {
  plan: 'saver' | 'pro' | 'founding';
  is_pro: boolean;
  trial: { ends_at: string; days_left: number } | null;
  subscription: {
    id: string;
    status: string;
    tier: Tier | null;
    interval: 'month' | 'year' | null;
    price_id: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  } | null;
  was_founding: boolean;
  has_stripe_customer: boolean;
  founding_seats: { claimed: number; cap: number };
}

async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ────────────────────────────────────────────────────────────────────
// Tier copy (kept close to marketing PricingToggle for consistency)
// ────────────────────────────────────────────────────────────────────

const saverFeatures = [
  '100 saved pages',
  '20 AI chat messages / month',
  '10 MCP calls / month',
  'Full-text search',
  'Chrome extension',
];

const proFeatures = [
  'Unlimited saves',
  '500 AI chat msgs / month',
  'Unlimited MCP · any model',
  'Full-text search',
  'Chrome extension + New Tab',
  'Conversation history & citations',
  'Priority support',
];

const foundingFeatures = [
  'Everything in Pro',
  'Locked at $129 / year while subscribed',
  'Save $51 / year vs Pro list',
  'First 100 supporters only',
];

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

const VALID_TIERS: Tier[] = ['pro_monthly', 'pro_yearly', 'founding_yearly'];

export function Upgrade() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const autostartFiredRef = useRef(false);

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // CTA in-flight state, keyed by tier
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);

  // Confirmation modal (CASE B: existing subscriber switching)
  const [switchTarget, setSwitchTarget] = useState<Tier | null>(null);
  const [preview, setPreview] = useState<{ amount_due_cents: number; currency: string; next_billing_date: string | null } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [switchSubmitting, setSwitchSubmitting] = useState(false);

  // Downgrade-to-Saver modal (CASE C: paying user cancels at period end)
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [isAnnual, setIsAnnual] = useState(true);

  // Resolve the upgrade_intent_id for this /upgrade visit. Three cases:
  //   1) Arrived via tracked CTA → ?upgrade_intent_id= present, use it
  //   2) Direct visit (typed URL, refresh, deep link) → synthesize once via
  //      the module-level Map (StrictMode-safe), write into URL via
  //      history.replaceState, and fire upgrade_intent with trigger=direct.
  //   3) Re-mounting under StrictMode → Map lookup returns the same UUID
  //      from the first mount, no second synthesis.
  const upgradeIntentIdRef = useRef<string | null>(null);
  const upgradePageViewedFiredRef = useRef(false);
  if (upgradeIntentIdRef.current === null) {
    const fromUrl = searchParams.get('upgrade_intent_id');
    if (fromUrl) {
      upgradeIntentIdRef.current = fromUrl;
    } else if (typeof window !== 'undefined') {
      const stateKey: string =
        (window.history.state && typeof window.history.state.key === 'string'
          ? window.history.state.key
          : '') || 'no_state_key';
      const existing = directVisitIntentMap.get(stateKey);
      if (existing) {
        upgradeIntentIdRef.current = existing;
      } else {
        // Generate via beginUpgradeIntent so the upgrade_intent event fires
        // with trigger_source='direct'. This is the *only* place that emits
        // upgrade_intent for a direct visit — banner/modal/settings paths
        // emit before they navigate.
        const id = analytics.beginUpgradeIntent('direct', {
          destination_path: '/upgrade',
        });
        directVisitIntentMap.set(stateKey, id);
        upgradeIntentIdRef.current = id;
        // Reflect into the URL so a refresh keeps the same id without re-firing.
        try {
          const next = new URLSearchParams(window.location.search);
          next.set('upgrade_intent_id', id);
          next.set('from', 'direct');
          window.history.replaceState(window.history.state, '', `${window.location.pathname}?${next.toString()}`);
        } catch { /* no-op */ }
      }
    }
  }
  const upgradeIntentId = upgradeIntentIdRef.current ?? undefined;
  const fromTrigger = searchParams.get('from') ?? 'direct';

  // ─────────── Auth gate ───────────

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  // ─────────── Load billing status ───────────

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiFetch<BillingStatus>('/api/billing/status');
      setStatus(data);
    } catch (err) {
      toast({ title: 'Failed to load plan status', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setStatusLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user) fetchStatus();
  }, [user, fetchStatus]);

  // upgrade_page_viewed fires once after status lands. Carries the
  // upgrade_intent_id so funnels can stitch click → page-view → plan-pick.
  useEffect(() => {
    if (statusLoading || !status) return;
    if (upgradePageViewedFiredRef.current) return;
    upgradePageViewedFiredRef.current = true;
    analytics.track('upgrade_page_viewed', {
      upgrade_intent_id: upgradeIntentId,
      from: fromTrigger,
      current_plan: status.plan,
      usage_state: undefined,
    });
  }, [statusLoading, status, upgradeIntentId, fromTrigger]);

  // ─────────── Derived state ───────────

  // CASE A vs CASE B detection: existing active subscriber gets the in-place
  // change-plan flow; everyone else (Saver, expired trial, trialing-no-card)
  // goes through Stripe Checkout.
  const isSubscriber = !!(status?.subscription && ['active', 'trialing', 'past_due'].includes(status.subscription.status));
  const currentTier: Tier | null = status?.subscription?.tier ?? null;

  // Per locked decision: paying users (subscribers) cannot enter the Founding
  // cohort via a plan switch — Founding is for first-time buyers only.
  // Anyone without an active sub (Saver, trialing-no-card, expired-trial) can
  // still claim a seat if any remain.
  const seatsLeft = status ? status.founding_seats.cap - status.founding_seats.claimed : 0;
  const foundingAvailable = !isSubscriber && seatsLeft > 0;

  // ─────────── Click → either checkout (CASE A) or confirm-switch (CASE B) ───────────

  const handleSelectTier = async (tier: Tier) => {
    if (!status) return;
    if (tier === currentTier) return;

    setPendingTier(tier);

    // upgrade_plan_picked fires immediately — even if the request errors,
    // we want the funnel to record the picked-tier moment for drop-off analysis.
    analytics.track('upgrade_plan_picked', {
      upgrade_intent_id: upgradeIntentId,
      plan: tier,
      interval: tier === 'pro_monthly' ? 'month' : 'year',
      from: fromTrigger,
    });

    try {
      if (isSubscriber) {
        // CASE B — fetch proration preview and open confirm modal
        setPreviewLoading(true);
        setSwitchTarget(tier);
        try {
          const prev = await apiFetch<typeof preview & object>(`/api/billing/preview-change?tier=${encodeURIComponent(tier)}`);
          setPreview(prev);
        } catch (err) {
          // Preview is best-effort; if it fails, show modal with a generic note.
          // eslint-disable-next-line no-console
          console.warn('preview failed', err);
          setPreview(null);
        } finally {
          setPreviewLoading(false);
        }
      } else {
        // CASE A — Stripe Checkout Session, redirect away
        // First-touch UTM snapshot rides on the request body so the server
        // can persist it to billing_attribution (durable across renewals).
        const firstTouch = analytics.getFirstTouch();
        const { url, stripe_session_id } = await apiFetch<{ url: string; stripe_session_id?: string }>('/api/billing/checkout', {
          method: 'POST',
          body: JSON.stringify({
            tier,
            // Thread the intent id into the checkout call. The server
            // persists it to billing_attribution and embeds in Stripe
            // metadata (session + subscription_data) so the webhook can
            // stitch back to trigger_source on every future lifecycle event.
            upgrade_intent_id: upgradeIntentId,
            trigger_source: fromTrigger,
            from_source: fromTrigger,
            first_utm_source: firstTouch.first_utm_source,
            first_utm_medium: firstTouch.first_utm_medium,
            first_utm_campaign: firstTouch.first_utm_campaign,
            first_utm_content: firstTouch.first_utm_content,
            first_utm_term: firstTouch.first_utm_term,
          }),
        });
        analytics.track('checkout_redirect_started', {
          upgrade_intent_id: upgradeIntentId,
          plan: tier,
          interval: tier === 'pro_monthly' ? 'month' : 'year',
          stripe_session_id,
          from: fromTrigger,
        });
        // Stash so the post-checkout return surface (Settings ?billing=...)
        // can correlate without a server roundtrip. 30-min TTL — anything
        // older than that is almost certainly a different session.
        try {
          window.localStorage.setItem('solem_pending_checkout', JSON.stringify({
            upgrade_intent_id: upgradeIntentId,
            plan: tier,
            interval: tier === 'pro_monthly' ? 'month' : 'year',
            from: fromTrigger,
            stripe_session_id,
            ts: Date.now(),
          }));
        } catch { /* localStorage disabled — analytics will still get the server event */ }
        window.location.href = url;
      }
    } catch (err) {
      toast({ title: 'Something went wrong', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
      setPendingTier(null);
    }
  };

  const handleConfirmSwitch = async () => {
    if (!switchTarget) return;
    setSwitchSubmitting(true);
    try {
      await apiFetch('/api/billing/change-plan', {
        method: 'POST',
        body: JSON.stringify({ tier: switchTarget }),
      });
      toast({ title: 'Plan updated', description: 'Your subscription has been switched.' });
      setSwitchTarget(null);
      setPreview(null);
      setPendingTier(null);
      // Refresh status from server
      fetchStatus();
      // Send user back to Settings
      navigate('/settings#plan');
    } catch (err) {
      toast({ title: 'Plan change failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setSwitchSubmitting(false);
    }
  };

  const handleCancelSwitch = () => {
    setSwitchTarget(null);
    setPreview(null);
    setPendingTier(null);
  };

  // ─────────── Downgrade-to-Saver (cancel-at-period-end) ───────────

  const handleOpenCancel = async () => {
    setCancelOpen(true);
    setUsageLoading(true);
    try {
      const data = await apiFetch<UsageResponse>('/api/billing/usage');
      setUsage(data);
    } catch (err) {
      // Modal still opens; the table just shows "—" for usage.
      // eslint-disable-next-line no-console
      console.warn('usage fetch failed', err);
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setCancelSubmitting(true);
    try {
      await apiFetch<{ ok: true; ends_at: string }>('/api/billing/cancel-subscription', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast({
        title: 'Subscription canceled',
        description: 'You’ll keep access until the end of your current period.',
      });
      setCancelOpen(false);
      setUsage(null);
      fetchStatus();
    } catch (err) {
      toast({
        title: 'Cancel failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleDismissCancel = () => {
    setCancelOpen(false);
    setUsage(null);
  };

  // ─────────── Autostart from marketing link (?tier=…&autostart=1) ───────────
  // When a user clicks a tier-specific CTA on solem.ai, they land here with
  // an autostart hint. After status loads, fire handleSelectTier once. The
  // URL is cleaned so a refresh doesn't re-trigger checkout.
  useEffect(() => {
    if (autostartFiredRef.current) return;
    if (statusLoading || !status) return;
    if (searchParams.get('autostart') !== '1') return;

    const tier = searchParams.get('tier') as Tier | null;
    if (!tier || !VALID_TIERS.includes(tier)) return;
    if (tier === currentTier) {
      // Already on this tier — just clean the URL.
      setSearchParams({}, { replace: true });
      return;
    }

    autostartFiredRef.current = true;
    setSearchParams({}, { replace: true });
    void handleSelectTier(tier);
  }, [statusLoading, status, searchParams, setSearchParams, currentTier, handleSelectTier]);

  // ─────────── Render ───────────

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        user={user}
        onSearch={(q) => navigate(`/search?q=${encodeURIComponent(q)}`)}
        onSignOut={async () => { await supabase.auth.signOut(); navigate('/'); }}
        isLoading={authLoading}
      />

      <main className="container max-w-5xl mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-5">
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-neutral-900">Choose your plan</h1>
          <p className="mt-3 text-base font-light text-neutral-600/80">
            Save anything from the web. Search it. Plug it into your AI agent.
          </p>
        </div>

        {/* Annual / Monthly toggle */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <button
            onClick={() => setIsAnnual(true)}
            className={`text-sm font-medium px-3 py-1.5 rounded-sm transition-colors ${
              isAnnual ? 'bg-neutral-100 text-neutral-900 ring-1 ring-neutral-200' : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900'
            }`}
          >
            Annual
            <span className="ml-1.5 text-[10px] uppercase tracking-wide opacity-80">2 mo free</span>
          </button>
          <button
            onClick={() => setIsAnnual(false)}
            className={`text-sm font-medium px-3 py-1.5 rounded-sm transition-colors ${
              !isAnnual ? 'bg-neutral-100 text-neutral-900 ring-1 ring-neutral-200' : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900'
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Cards */}
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-start gap-6 lg:grid-cols-3">
          <SaverCard
            isCurrent={!isSubscriber && status?.plan === 'saver' && !status?.trial}
            isSubscriber={isSubscriber}
            cancelAtPeriodEnd={!!status?.subscription?.cancel_at_period_end}
            periodEnd={status?.subscription?.current_period_end ?? null}
            onDowngrade={handleOpenCancel}
          />
          <ProCard
            isAnnual={isAnnual}
            currentTier={currentTier}
            isSubscriber={isSubscriber}
            isTrialing={!!status?.trial}
            pendingTier={pendingTier}
            statusLoading={statusLoading}
            onSelect={handleSelectTier}
          />
          {foundingAvailable && (
            <FoundingCard
              claimed={status!.founding_seats.claimed}
              cap={status!.founding_seats.cap}
              pendingTier={pendingTier}
              statusLoading={statusLoading}
              onSelect={handleSelectTier}
            />
          )}
          {!foundingAvailable && status?.subscription?.tier === 'founding_yearly' && (
            <FoundingCard
              claimed={status.founding_seats.claimed}
              cap={status.founding_seats.cap}
              isCurrent
              pendingTier={null}
              statusLoading={false}
              onSelect={() => {}}
            />
          )}
        </div>

        <p className="mt-10 text-center text-sm text-neutral-400">
          14-day free trial · 30-day money-back guarantee · Cancel anytime
        </p>
      </main>

      {/* CASE C — Downgrade to Saver confirmation (cancel at period end) */}
      <AlertDialog open={cancelOpen} onOpenChange={(open) => { if (!open) handleDismissCancel(); }}>
        <AlertDialogContent className="max-w-2xl p-0 gap-0 bg-neutral-50 ring-1 ring-neutral-200 sm:rounded-sm overflow-hidden">
          {/* Header — white panel */}
          <div className="bg-white px-8 pt-8 pb-6 border-b border-neutral-200">
            <AlertDialogHeader className="text-left space-y-2">
              <AlertDialogTitle className="text-2xl font-light tracking-tight text-neutral-900">
                Leaving {status?.subscription?.tier === 'founding_yearly' ? 'Founding' : 'Pro'}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[15px] text-neutral-600 font-light leading-relaxed">
                Your benefits run until{' '}
                <strong className="text-neutral-900 font-medium">
                  {status?.subscription?.current_period_end
                    ? new Date(status.subscription.current_period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'the end of your current period'}
                </strong>
                . After that, you drop to Saver and these caps kick in.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>

          {/* Body — gray panel */}
          <div className="px-8 py-6 space-y-5">
            <div className="rounded-sm overflow-hidden bg-white ring-1 ring-neutral-200">
              <div className="grid grid-cols-[1fr_6rem_6rem] px-4 py-2.5 text-[10px] font-medium uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
                <div>Features</div>
                <div className="text-right">Yours</div>
                <div className="text-right">Saver cap</div>
              </div>
              {usageLoading ? (
                <div className="px-4 py-6 text-center text-neutral-400 text-sm">Loading usage…</div>
              ) : (
                <>
                  <ImpactRow label="Saved pages" sub="total" used={usage?.saves.used} cap={SAVER_CAPS.saves} />
                  <ImpactRow label="AI chat" sub="this month" used={usage?.ai_message.used} cap={SAVER_CAPS.ai_message} />
                  <ImpactRow label="MCP calls" sub="this month" used={usage?.mcp_call.used} cap={SAVER_CAPS.mcp_call} last />
                </>
              )}
            </div>

            <p className="text-sm text-neutral-600 font-light leading-relaxed">
              Existing pages stay searchable. You just can't add more in any over-cap category until you delete down to the limit (saves) or the month rolls over (chat &amp; MCP).
            </p>

            {status?.subscription?.tier === 'founding_yearly' && (
              <div className="rounded-sm bg-gradient-to-b from-orange-50/70 to-orange-50/20 ring-1 ring-[#FF3100]/25 px-4 py-3.5 flex gap-3">
                <Flame className="h-4 w-4 text-[#FF3100] fill-[#FF3100] flex-none mt-0.5" />
                <p className="text-sm text-[#9B1B00] font-light leading-relaxed">
                  <span className="font-medium text-[#FF3100]">Founding seats don't come back.</span>{' '}
                  Your $129/year rate disappears the moment you cancel. Re-subscribing later means Pro list price — $180/year, no exceptions.
                </p>
              </div>
            )}
          </div>

          {/* Footer — white panel, Stay primary blue (far left), Downgrade neutral→red hover (far right) */}
          <AlertDialogFooter className="px-8 py-4 bg-white border-t border-neutral-200 gap-2 sm:gap-0 sm:justify-between sm:space-x-0">
            <AlertDialogCancel
              disabled={cancelSubmitting}
              className="mt-0 bg-blue-600 text-white border-0 hover:bg-blue-700 hover:text-white rounded-sm shadow-none focus-visible:ring-blue-600"
            >
              Stay on {status?.subscription?.tier === 'founding_yearly' ? 'Founding' : 'Pro'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={cancelSubmitting}
              className="bg-white text-neutral-700 ring-1 ring-neutral-300 hover:bg-red-600 hover:text-white hover:ring-red-600 transition-colors rounded-sm shadow-none"
            >
              {cancelSubmitting ? 'Canceling…' : 'Downgrade to Saver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CASE B confirmation modal */}
      <AlertDialog open={!!switchTarget} onOpenChange={(open) => { if (!open) handleCancelSwitch(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch to {switchTarget ? tierLabel(switchTarget) : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {previewLoading ? (
                <span className="block">Calculating proration…</span>
              ) : preview ? (
                <>
                  <span className="block">
                    You'll be charged <strong>{formatMoney(preview.amount_due_cents, preview.currency)}</strong> today
                    (prorated for the rest of your current period).
                  </span>
                  {preview.next_billing_date && (
                    <span className="block text-sm">
                      Next billing date: <strong>{new Date(preview.next_billing_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</strong>
                    </span>
                  )}
                </>
              ) : (
                <span className="block">
                  Stripe will prorate your current period — you'll be charged or credited the difference automatically.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={switchSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSwitch}
              disabled={switchSubmitting || previewLoading}
            >
              {switchSubmitting ? 'Switching…' : 'Confirm switch'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Card components
// ────────────────────────────────────────────────────────────────────

function SaverCard({
  isCurrent,
  isSubscriber,
  cancelAtPeriodEnd,
  periodEnd,
  onDowngrade,
}: {
  isCurrent: boolean;
  isSubscriber: boolean;
  cancelAtPeriodEnd: boolean;
  periodEnd: string | null;
  onDowngrade: () => void;
}) {
  const ctaContent = (() => {
    if (isCurrent) {
      return <Button variant="outline" disabled className="w-full rounded-sm">Your current plan</Button>;
    }
    if (isSubscriber && cancelAtPeriodEnd && periodEnd) {
      return (
        <Button variant="outline" disabled className="w-full rounded-sm">
          Active until {new Date(periodEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </Button>
      );
    }
    if (isSubscriber) {
      return (
        <Button
          variant="outline"
          onClick={onDowngrade}
          className="w-full rounded-sm text-neutral-700 hover:bg-red-50 hover:text-red-700 hover:ring-red-200"
        >
          Downgrade to Saver
        </Button>
      );
    }
    return <Button variant="outline" disabled className="w-full rounded-sm">Default after trial</Button>;
  })();

  return (
    <div className="relative rounded-sm p-7 ring-1 ring-neutral-200 bg-white">
      <h3 className="text-base font-medium text-neutral-500">Saver</h3>
      <p className="mt-4 flex items-baseline gap-x-2">
        <span className="text-3xl font-light tracking-tight text-neutral-900">Free</span>
      </p>
      <p className="text-xs text-neutral-400 mt-1">Forever, after your trial</p>
      <p className="mt-4 text-sm text-neutral-600">A taste of Solem, with hard caps.</p>
      <ul className="mt-5 space-y-2.5 text-sm text-neutral-600">
        {saverFeatures.map((f) => (
          <li key={f} className="flex gap-x-2.5">
            <Check className="h-4 w-4 flex-none text-neutral-400 mt-0.5" />
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-7">{ctaContent}</div>
    </div>
  );
}

function ProCard({
  isAnnual,
  currentTier,
  isSubscriber,
  isTrialing,
  pendingTier,
  statusLoading,
  onSelect,
}: {
  isAnnual: boolean;
  currentTier: Tier | null;
  isSubscriber: boolean;
  isTrialing: boolean;
  pendingTier: Tier | null;
  statusLoading: boolean;
  onSelect: (tier: Tier) => void;
}) {
  const tier: Tier = isAnnual ? 'pro_yearly' : 'pro_monthly';
  const isCurrent = currentTier === tier;
  const isPending = pendingTier === tier;

  const price = isAnnual ? '$15' : '$19';
  const note = isAnnual ? 'Billed $180/year — 2 months free' : 'Billed monthly';

  let cta = isSubscriber ? `Switch to ${isAnnual ? 'yearly' : 'monthly'}` : (isTrialing ? 'Subscribe to Pro' : 'Start 14-day free trial');
  if (isCurrent) cta = 'Your current plan';
  if (isPending) cta = 'Loading…';

  return (
    <div className="relative rounded-sm p-7 ring-1 ring-neutral-200 bg-neutral-100 text-neutral-900 shadow-2xl shadow-neutral-900/5 lg:scale-[1.02]">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-neutral-900">Pro</h3>
        <span className="text-[10px] font-medium px-2.5 py-0.5 rounded-full bg-blue-600 text-white uppercase tracking-wide">Most popular</span>
      </div>
      <p className="mt-4 flex items-baseline gap-x-2">
        <span className="text-3xl font-light tracking-tight text-neutral-900">{price}</span>
        <span className="text-sm text-neutral-500">/month</span>
      </p>
      <p className="text-xs text-neutral-500 mt-1">{note}</p>
      <p className="mt-4 text-sm text-neutral-700">No caps. Made for daily use.</p>
      <ul className="mt-5 space-y-2.5 text-sm text-neutral-700">
        {proFeatures.map((f) => (
          <li key={f} className="flex gap-x-2.5">
            <Check className="h-4 w-4 flex-none text-blue-600 mt-0.5" />
            {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSelect(tier)}
        disabled={isCurrent || isPending || statusLoading}
        className="mt-7 w-full rounded-sm bg-blue-600 hover:bg-blue-700 text-white disabled:bg-neutral-200 disabled:text-neutral-500 disabled:opacity-100"
      >
        {cta}
      </Button>
      {!isCurrent && !isSubscriber && (
        <p className="mt-2.5 text-center text-[11px] text-neutral-400">
          No card required · 30-day money-back
        </p>
      )}
    </div>
  );
}

function FoundingCard({
  claimed,
  cap,
  isCurrent = false,
  pendingTier,
  statusLoading,
  onSelect,
}: {
  claimed: number;
  cap: number;
  isCurrent?: boolean;
  pendingTier: Tier | null;
  statusLoading: boolean;
  onSelect: (tier: Tier) => void;
}) {
  const tier: Tier = 'founding_yearly';
  const isPending = pendingTier === tier;
  const seatsLeft = Math.max(0, cap - claimed);

  let cta = 'Claim founding seat';
  if (isCurrent) cta = 'Your current plan';
  if (isPending) cta = 'Loading…';

  return (
    <div className="relative rounded-sm p-7 ring-1 ring-[#FF3100]/30 bg-gradient-to-b from-orange-50/40 to-white">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-[#FF3100]">Founding</h3>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-0.5 rounded-full bg-[#FF3100] text-white uppercase tracking-wide">
          <Flame size={10} fill="currentColor" /> Limited
        </span>
      </div>
      <p className="mt-4 flex items-baseline gap-x-2">
        <span className="text-3xl font-light tracking-tight text-neutral-900">$10.75</span>
        <span className="text-sm text-neutral-500">/month</span>
      </p>
      <p className="text-xs text-neutral-500 mt-1">Billed $129/year — grandfathered for life</p>

      <div className="mt-4 rounded-sm border border-[#FF3100]/20 bg-[#FF3100]/5 px-2.5 py-2">
        <div className="flex items-center justify-between text-xs font-medium text-[#FF3100]">
          <span>{claimed} of {cap} claimed</span>
          <span>{seatsLeft} left</span>
        </div>
        <div className="mt-1.5 h-1 w-full rounded-full bg-[#FF3100]/15 overflow-hidden">
          <div
            className="h-full bg-[#FF3100] transition-all"
            style={{ width: `${(claimed / cap) * 100}%` }}
          />
        </div>
      </div>

      <p className="mt-4 text-sm text-neutral-600">Discounted Pro for early believers.</p>
      <ul className="mt-5 space-y-2.5 text-sm text-neutral-600">
        {foundingFeatures.map((f) => (
          <li key={f} className="flex gap-x-2.5">
            <Check className="h-4 w-4 flex-none text-[#FF3100] mt-0.5" />
            {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSelect(tier)}
        disabled={isCurrent || isPending || statusLoading || seatsLeft === 0}
        className="mt-7 w-full rounded-sm bg-[#FF3100] hover:bg-[#e62d00] text-white disabled:bg-neutral-300 disabled:text-neutral-500 disabled:opacity-100"
      >
        {cta}
      </Button>
      {!isCurrent && (
        <p className="mt-2.5 text-center text-[11px] text-neutral-400">
          Cancel forfeits the rate
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tiny helpers
// ────────────────────────────────────────────────────────────────────

function tierLabel(t: Tier): string {
  if (t === 'pro_monthly') return 'Pro · Monthly';
  if (t === 'pro_yearly') return 'Pro · Yearly';
  if (t === 'founding_yearly') return 'Founding · Yearly';
  return 'Pro';
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

// Downgrade-impact table row. Fixed-width right columns (6rem each) so
// "Yours" and "Saver cap" align across all rows even when one row has an
// "N OVER" badge stacked below the number. `sub` clarifies cadence (total
// vs this month).
function ImpactRow({
  label,
  sub,
  used,
  cap,
  last,
}: {
  label: string;
  sub: string;
  used: number | undefined;
  cap: number;
  last?: boolean;
}) {
  const overflow = typeof used === 'number' ? used - cap : null;
  const isOver = overflow !== null && overflow > 0;
  return (
    <div
      className={`grid grid-cols-[1fr_6rem_6rem] px-4 py-3 items-center ${last ? '' : 'border-b border-neutral-100'}`}
    >
      <div className="min-w-0">
        <div className="text-sm text-neutral-900 truncate">{label}</div>
        <div className="text-[11px] text-neutral-500">{sub}</div>
      </div>
      <div className="text-right">
        <div className={`tabular-nums text-sm ${isOver ? 'text-red-700 font-medium' : 'text-neutral-900'}`}>
          {typeof used === 'number' ? used : '—'}
        </div>
        {isOver && (
          <div className="text-[10px] font-medium text-red-700 uppercase tracking-wide">
            {overflow} over
          </div>
        )}
      </div>
      <div className="text-right text-sm tabular-nums text-neutral-500">{cap}</div>
    </div>
  );
}

